// HTTP Service with enhanced error handling
import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { API_BASE_URL } from "../constants";
import { ApiResponse, normalizeApiResponse } from "../types/api";
import { isJwtExpired } from "../utils/jwt";
import getValidFirebaseToken from "../utils/valid_firebase_token";

// Extend AxiosRequestConfig to include metadata
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  metadata?: {
    startTime: number;
  };
}

// API Error response interface
interface ApiErrorResponse {
  success?: boolean;
  message?: string;
  error?: {
    message?: string;
    code?: string;
    details?: unknown;
  } | string;
  data?: unknown;
  meta?: unknown;
  details?: any;
}

// Custom exception classes
export class ApiException extends Error {
  constructor(message: string, public statusCode?: number, public data?: any) {
    super(message);
    this.name = "ApiException";
  }
}

export class ValidationException extends ApiException {
  constructor(message: string, data?: any) {
    super(message, 422, data);
    this.name = "ValidationException";
  }
}

export class AuthenticationException extends ApiException {
  constructor(message: string = "Authentication failed") {
    super(message, 401);
    this.name = "AuthenticationException";
  }
}

export class NetworkException extends ApiException {
  constructor(message: string = "Network error") {
    super(message, 0);
    this.name = "NetworkException";
  }
}

class HttpService {
  private axiosInstance;

  constructor() {
    if (!API_BASE_URL) {
      throw new Error(
        "API_BASE_URL is not defined! Check your .env or .env.production file and restart the app."
      );
    }

    this.axiosInstance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        "Content-Type": "application/json",
      },
    });
    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      async (config: ExtendedAxiosRequestConfig) => {
        try {
          let token = localStorage.getItem("authToken");

          if (token && isJwtExpired(token)) {
            token = await getValidFirebaseToken();
          }

          if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
          }

          // Add request timestamp for debugging
          config.metadata = { startTime: Date.now() };

          return config;
        } catch (error) {
          return Promise.reject(
            new AuthenticationException("Failed to authenticate request")
          );
        }
      },
      (error) => {
        return Promise.reject(
          new NetworkException("Failed to prepare request")
        );
      }
    );

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: AxiosError) => {
        this.handleError(error);
        return Promise.reject(error);
      }
    );
  }

  private handleError(error: AxiosError) {
    const response = error.response;

    if (!response) {
      throw new NetworkException(
        "Network error. Please check your connection and try again."
      );
    }

    const payload = (response.data || {}) as ApiErrorResponse;
    const backendMessage =
      (typeof payload.error === "object" && payload.error?.message) ||
      payload.message ||
      (typeof payload.error === "string" ? payload.error : undefined);

    switch (response.status) {
      case 400:
        throw new ApiException(backendMessage || "Bad request.", 400, response.data);

      case 401:
        localStorage.removeItem("authToken");
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
        throw new AuthenticationException("Authentication required. Please sign in again.");

      case 403:
        throw new ApiException(
          "You do not have permission to perform this action.",
          403,
          response.data
        );

      case 404:
        throw new ApiException(
          backendMessage || "The requested resource was not found.",
          404,
          response.data
        );

      case 422:
        throw new ValidationException(backendMessage || "Validation error.", response.data);

      case 429:
        throw new ApiException(
          backendMessage || "Too many requests. Please try again later.",
          429,
          response.data
        );

      case 500:
        throw new ApiException(
          backendMessage || "Internal server error.",
          500,
          response.data
        );

      case 502:
      case 503:
      case 504:
        throw new ApiException(
          "Service is temporarily unavailable.",
          response.status,
          response.data
        );

      default:
        throw new ApiException(
          backendMessage || `Request failed with status ${response.status}.`,
          response.status,
          response.data
        );
    }
  }

  // Generic HTTP methods with response normalization
  async get<T>(url: string, config?: any): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.get(url, config);
    return normalizeApiResponse(response.data);
  }

  async post<T>(
    url: string,
    data?: any,
    config?: any
  ): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.post(url, data, config);
    return normalizeApiResponse(response.data);
  }

  async put<T>(url: string, data?: any, config?: any): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.put(url, data, config);
    return normalizeApiResponse(response.data);
  }

  async delete<T>(url: string, config?: any): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.delete(url, config);
    return normalizeApiResponse(response.data);
  }

  // Raw methods for backward compatibility
  async getRaw(url: string, config?: any) {
    return this.axiosInstance.get(url, config);
  }

  async postRaw(url: string, data?: any, config?: any) {
    return this.axiosInstance.post(url, data, config);
  }

  async putRaw(url: string, data?: any, config?: any) {
    return this.axiosInstance.put(url, data, config);
  }

  async deleteRaw(url: string, config?: any) {
    return this.axiosInstance.delete(url, config);
  }
}

export default new HttpService();
